<template>
    <ExpandableCard :expand-id="parcela.n_pedido">
        <template #summaryIcon>
            <div class="tw:hidden tw:w-10 tw:h-10 tw:rounded-full tw:bg-slate-100 tw:sm:flex tw:items-center tw:justify-center tw:text-[var(--altcolor)]">
                <i class="material-icons tw:text-xl">grid_on</i>
            </div>
        </template>
        <template #summaryContent>
            <span class="tw:text-base tw:font-extrabold tw:text-slate-900">
                {{ $t('perfil.parcelas_pedido_numero', { n: parcela.n_pedido }) }}
            </span>
        </template>
        <template #summaryActions>
            <div class="tw:text-right">
                <span class="tw:text-sm tw:font-semibold tw:text-slate-900">{{ formatPrice(parcela.price_total_pedido) }}</span>
                <BadgeFront :label="statusLabel" :variant="statusVariant" dot customClass="tw:mt-1" />
            </div>
        </template>

        <!-- Contenido expandido -->
        <div class="tw:flex tw:justify-end tw:mb-4">
            <Button v-if="parcela.pdf_url" as="a" :href="parcela.pdf_url" target="_blank" variant="danger" iconName="download">
                {{ $t('perfil.descargar_pdf') }}
            </Button>
        </div>
        <div class="tw:grid tw:grid-cols-1 tw:sm:grid-cols-2 tw:gap-4">
            <div class="tw:flex tw:items-center tw:gap-3 tw:min-w-0">
                <div class="tw:w-8 tw:h-8 tw:rounded-full tw:bg-slate-100 tw:flex tw:items-center tw:justify-center tw:flex-shrink-0 tw:text-[var(--altcolor)]">
                    <i class="material-icons tw:text-sm">person</i>
                </div>
                <div class="tw:min-w-0">
                    <p class="tw:text-xs tw:font-bold tw:text-slate-400 tw:uppercase tw:tracking-wider tw:mb-1">{{ $t('perfil.parcelas_nombre_completo') }}</p>
                    <p class="tw:text-sm tw:text-slate-900 tw:break-words">{{ parcela.datos_comprador?.nombre || '—' }} {{ parcela.datos_comprador?.apellidos || '—' }}</p>
                </div>
            </div>
            <div class="tw:flex tw:items-center tw:gap-3 tw:min-w-0">
                <div class="tw:w-8 tw:h-8 tw:rounded-full tw:bg-slate-100 tw:flex tw:items-center tw:justify-center tw:flex-shrink-0 tw:text-[var(--altcolor)]">
                    <i class="material-icons tw:text-sm">email</i>
                </div>
                <div class="tw:min-w-0">
                    <p class="tw:text-xs tw:font-bold tw:text-slate-400 tw:uppercase tw:tracking-wider tw:mb-1">{{ $t('formularios.email') }}</p>
                    <p class="tw:text-sm tw:text-slate-900 tw:break-words">{{ parcela.datos_comprador?.email || '—' }}</p>
                </div>
            </div>
            <div class="tw:flex tw:items-center tw:gap-3 tw:min-w-0">
                <div class="tw:w-8 tw:h-8 tw:rounded-full tw:bg-slate-100 tw:flex tw:items-center tw:justify-center tw:flex-shrink-0 tw:text-[var(--altcolor)]">
                    <i class="material-icons tw:text-sm">receipt_long</i>
                </div>
                <div class="tw:min-w-0">
                    <p class="tw:text-xs tw:font-bold tw:text-slate-400 tw:uppercase tw:tracking-wider tw:mb-1">{{ $t('perfil.n_pedido') }}</p>
                    <p class="tw:text-sm tw:text-slate-900 tw:break-words">{{ parcela.n_pedido || '—' }}</p>
                </div>
            </div>
            <div class="tw:flex tw:items-center tw:gap-3 tw:min-w-0">
                <div class="tw:w-8 tw:h-8 tw:rounded-full tw:bg-slate-100 tw:flex tw:items-center tw:justify-center tw:flex-shrink-0 tw:text-[var(--altcolor)]">
                    <i class="material-icons tw:text-sm">calendar_today</i>
                </div>
                <div class="tw:min-w-0">
                    <p class="tw:text-xs tw:font-bold tw:text-slate-400 tw:uppercase tw:tracking-wider tw:mb-1">{{ $t('perfil.fecha_compra') }}</p>
                    <p class="tw:text-sm tw:text-slate-900 tw:break-words">{{ formatDate(parcela.fecha_compra) }}</p>
                </div>
            </div>
            <div class="tw:flex tw:items-center tw:gap-3 tw:min-w-0 tw:col-span-1 tw:sm:col-span-2">
                <div class="tw:w-8 tw:h-8 tw:rounded-full tw:bg-slate-100 tw:flex tw:items-center tw:justify-center tw:flex-shrink-0 tw:text-[var(--altcolor)]">
                    <i class="material-icons tw:text-sm">payments</i>
                </div>
                <div class="tw:min-w-0">
                    <p class="tw:text-xs tw:font-bold tw:text-slate-400 tw:uppercase tw:tracking-wider tw:mb-1">{{ $t('perfil.parcelas_total') }}</p>
                    <p class="tw:text-sm tw:text-slate-900 tw:font-semibold">{{ formatPrice(parcela.price_total_pedido) }}</p>
                </div>
            </div>
        </div>

        <div v-if="parcela.parcelas?.length" class="tw:mt-4">
            <p class="tw:text-xs tw:font-bold tw:text-slate-400 tw:uppercase tw:tracking-wider tw:mb-2">{{ $t('perfil.parcelas_lista') }}</p>
            <div class="tw:space-y-2">
                <div
                    v-for="subParcela in parcela.parcelas"
                    :key="subParcela.parcela_id"
                    class="tw:flex tw:flex-col tw:sm:flex-row tw:justify-between tw:sm:items-center tw:gap-2 tw:bg-white tw:p-3 tw:rounded-lg tw:border tw:border-gray-200"
                >
                    <div class="tw:flex tw:items-center tw:gap-3 tw:min-w-0">
                        <i class="material-icons tw:text-lg tw:text-[var(--altcolor)] tw:flex-shrink-0">grid_on</i>
                        <span class="tw:text-sm tw:text-slate-900 tw:break-words">{{ $t('perfil.parcelas_numero', { n: subParcela.parcela_numero }) }}</span>
                        <span class="tw:text-sm tw:text-slate-500 tw:break-words tw:flex-shrink-0">- {{ subParcela.zona?.nombre || '—' }}</span>
                    </div>
                    <div class="tw:flex tw:items-center tw:gap-3 tw:flex-shrink-0">
                        <span class="tw:text-sm tw:font-semibold tw:text-slate-900">{{ formatPrice(subParcela.parcela_ticket?.price) }}</span>
                        <Button v-if="subParcela.pdf_url" as="a" :href="subParcela.pdf_url" target="_blank" variant="danger" iconName="download" iconNameClass="tw:text-sm" customClass="tw:inline-flex tw:items-center tw:justify-center tw:w-8 tw:h-8 tw:p-0" />
                    </div>
                </div>
            </div>
        </div>
    </ExpandableCard>
</template>

<script setup>
    import { computed } from 'vue';
    import ExpandableCard from '@/modules/ui/ExpandableCard.vue';
    import BadgeFront from '@/modules/front/ui/BadgeFront.vue';
    import Button from '@/modules/front/ui/Button.vue';
    import { t } from '@/utils/i18n-helper';

    const props = defineProps({
        parcela: { type: Object, required: true },
    });

    const formatPrice = price => {
        if (!price && price !== 0) return '—';
        return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(price);
    };

    const formatDate = dateString => {
        if (!dateString || typeof dateString !== 'string') return '—';
        const parts = dateString.split(' ')[0].split('/');
        if (parts.length !== 3) return dateString;
        const date = new Date(parts[2], parts[1] - 1, parts[0]);
        return date.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' }) + ' ' + dateString.split(' ')[1];
    };

    const statusLabel = computed(() => {
        const isCompleted = props.parcela.parcelas?.length > 0 && props.parcela.parcelas.every(p => p.parcela_ticket?.price);
        return isCompleted ? t('perfil.parcelas_completado') : t('perfil.parcelas_pendiente');
    });

    const statusVariant = computed(() => {
        const isCompleted = props.parcela.parcelas?.length > 0 && props.parcela.parcelas.every(p => p.parcela_ticket?.price);
        return isCompleted ? 'success' : 'warning';
    });
</script>
